import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, Volume2, Mic, Play, Loader2, Save, Check, RefreshCw } from 'lucide-react'

interface VoiceModalProps {
  isOpen: boolean
  onClose: () => void
}

interface VoiceSettings {
  voice_enabled: boolean
  voice_id: string
  speech_rate: number
  auto_play_responses: boolean
  wake_word_enabled: boolean
  voice_input_enabled: boolean
  continuous_listening: boolean
}

interface VoiceOption {
  id: string
  name: string
  description: string
  preview_url?: string
  gender?: string
}

interface TTSProvider {
  provider: string
  voices: VoiceOption[]
}

const getAuthToken = () => localStorage.getItem('niki_access_token')

// Fallback voices if API fails
const FALLBACK_VOICES: Record<string, VoiceOption[]> = {
  elevenlabs: [
    { id: 'rachel', name: 'Rachel', description: 'Warm & natural female', gender: 'female' },
    { id: 'bella', name: 'Bella', description: 'Soft & gentle female', gender: 'female' },
    { id: 'sarah', name: 'Sarah', description: 'Clear & professional female', gender: 'female' },
    { id: 'adam', name: 'Adam', description: 'Deep & confident male', gender: 'male' },
    { id: 'josh', name: 'Josh', description: 'Friendly & casual male', gender: 'male' },
    { id: 'sam', name: 'Sam', description: 'Energetic male', gender: 'male' },
  ],
  openai: [
    { id: 'nova', name: 'Nova', description: 'Warm and friendly', gender: 'female' },
    { id: 'shimmer', name: 'Shimmer', description: 'Soft and calm', gender: 'female' },
    { id: 'alloy', name: 'Alloy', description: 'Balanced and versatile', gender: 'neutral' },
    { id: 'echo', name: 'Echo', description: 'Clear and neutral', gender: 'male' },
    { id: 'onyx', name: 'Onyx', description: 'Deep and authoritative', gender: 'male' },
    { id: 'fable', name: 'Fable', description: 'Expressive and dynamic', gender: 'male' },
  ],
}

export function VoiceModal({ isOpen, onClose }: VoiceModalProps) {
  const [settings, setSettings] = useState<VoiceSettings>({
    voice_enabled: true,
    voice_id: 'rachel',
    speech_rate: 1.0,
    auto_play_responses: false,
    wake_word_enabled: false,
    voice_input_enabled: true,
    continuous_listening: false,
  })
  const [saving, setSaving] = useState(false)
  const [success, setSuccess] = useState(false)
  const [testPlaying, setTestPlaying] = useState(false)
  const [provider, setProvider] = useState<string>('elevenlabs')
  const [voices, setVoices] = useState<VoiceOption[]>([])
  const [loadingVoices, setLoadingVoices] = useState(false)

  useEffect(() => {
    if (isOpen) {
      loadSettings()
      loadVoices()
    }
  }, [isOpen])

  const loadSettings = async () => {
    try {
      const res = await fetch('/api/v1/settings/voice', {
        headers: { 'Authorization': `Bearer ${getAuthToken()}` },
      })
      if (res.ok) {
        const data = await res.json()
        setSettings(data)
      }
    } catch (err) {
      console.error('Failed to load voice settings:', err)
    }
  }

  const loadVoices = async () => {
    setLoadingVoices(true)
    try {
      const res = await fetch('/api/v1/settings/voices', {
        headers: { 'Authorization': `Bearer ${getAuthToken()}` },
      })
      if (res.ok) {
        const data: TTSProvider = await res.json()
        setProvider(data.provider)
        if (data.voices && data.voices.length > 0) {
          setVoices(data.voices)
        } else {
          // Use fallback voices for the provider
          setVoices(FALLBACK_VOICES[data.provider] || FALLBACK_VOICES.elevenlabs)
        }
      } else {
        // Fallback to ElevenLabs defaults
        setVoices(FALLBACK_VOICES.elevenlabs)
      }
    } catch (err) {
      console.error('Failed to load voices:', err)
      setVoices(FALLBACK_VOICES.elevenlabs)
    } finally {
      setLoadingVoices(false)
    }
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      const res = await fetch('/api/v1/settings/voice', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getAuthToken()}`,
        },
        body: JSON.stringify(settings),
      })

      if (res.ok) {
        setSuccess(true)
        setTimeout(() => {
          setSuccess(false)
          onClose()
        }, 1500)
      }
    } catch (err) {
      console.error('Failed to save settings:', err)
    } finally {
      setSaving(false)
    }
  }

  const testVoice = async () => {
    setTestPlaying(true)
    try {
      const res = await fetch('/api/v1/settings/tts/test', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getAuthToken()}`,
        },
        body: JSON.stringify({
          text: "Hello! I'm Niki, your digital assistant. How can I help you today?",
          voice: settings.voice_id,
          speed: settings.speech_rate,
        }),
      })

      if (res.ok) {
        const blob = await res.blob()
        const audioUrl = URL.createObjectURL(blob)
        const audio = new Audio(audioUrl)
        audio.onended = () => {
          setTestPlaying(false)
          URL.revokeObjectURL(audioUrl)
        }
        audio.onerror = (e) => {
          console.error('Audio playback error:', e)
          setTestPlaying(false)
          URL.revokeObjectURL(audioUrl)
        }
        try {
          await audio.play()
        } catch (playErr) {
          console.error('Audio play failed:', playErr)
          setTestPlaying(false)
          URL.revokeObjectURL(audioUrl)
        }
      } else {
        console.error('TTS request failed:', res.status)
        setTestPlaying(false)
      }
    } catch (err) {
      console.error('Failed to test voice:', err)
      setTestPlaying(false)
    }
  }

  const Toggle = ({ enabled, onChange }: { enabled: boolean; onChange: (v: boolean) => void }) => (
    <button
      onClick={() => onChange(!enabled)}
      className={`w-12 h-6 rounded-full transition-colors relative ${
        enabled ? 'bg-primary' : 'bg-white/20'
      }`}
    >
      <div
        className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
          enabled ? 'translate-x-7' : 'translate-x-1'
        }`}
      />
    </button>
  )

  if (!isOpen) return null

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-[#1a2e1a] rounded-2xl w-full max-w-md overflow-hidden max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-white/10 sticky top-0 bg-[#1a2e1a]">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/20 rounded-lg">
                <Volume2 className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-white">Voice & Sound</h2>
                <p className="text-xs text-white/50 capitalize">Provider: {provider}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-white/70" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            {/* Voice Output */}
            <div>
              <h3 className="text-sm font-medium text-white/50 uppercase tracking-wider mb-4">
                Voice Output
              </h3>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white">Voice Responses</p>
                    <p className="text-sm text-white/50">Enable voice for Niki's replies</p>
                  </div>
                  <Toggle
                    enabled={settings.voice_enabled}
                    onChange={(v) => setSettings({ ...settings, voice_enabled: v })}
                  />
                </div>

                {settings.voice_enabled && (
                  <>
                    {/* Voice Selection */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="text-sm text-white/70">Voice</label>
                        <button
                          onClick={loadVoices}
                          disabled={loadingVoices}
                          className="text-xs text-primary hover:text-primary/80 flex items-center gap-1"
                        >
                          <RefreshCw className={`w-3 h-3 ${loadingVoices ? 'animate-spin' : ''}`} />
                          Refresh
                        </button>
                      </div>
                      
                      {loadingVoices ? (
                        <div className="flex items-center justify-center py-8">
                          <Loader2 className="w-6 h-6 animate-spin text-primary" />
                        </div>
                      ) : (
                        <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                          {voices.map((voice) => (
                            <button
                              key={voice.id}
                              onClick={() => setSettings({ ...settings, voice_id: voice.id })}
                              className={`p-3 rounded-lg border text-left transition-colors ${
                                settings.voice_id === voice.id
                                  ? 'border-primary bg-primary/10'
                                  : 'border-white/10 hover:border-white/20'
                              }`}
                            >
                              <div className="flex items-center gap-2">
                                <p className="text-white font-medium text-sm">{voice.name}</p>
                                {voice.gender && (
                                  <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                                    voice.gender === 'female' ? 'bg-pink-500/20 text-pink-300' :
                                    voice.gender === 'male' ? 'bg-blue-500/20 text-blue-300' :
                                    'bg-white/10 text-white/50'
                                  }`}>
                                    {voice.gender}
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-white/50 truncate">{voice.description}</p>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Speech Rate */}
                    <div>
                      <label className="text-sm text-white/70 mb-2 block">
                        Speech Rate: {settings.speech_rate.toFixed(1)}x
                      </label>
                      <input
                        type="range"
                        min="0.5"
                        max="2"
                        step="0.1"
                        value={settings.speech_rate}
                        onChange={(e) => setSettings({ ...settings, speech_rate: parseFloat(e.target.value) })}
                        className="w-full accent-primary"
                      />
                      <div className="flex justify-between text-xs text-white/40">
                        <span>Slower</span>
                        <span>Faster</span>
                      </div>
                    </div>

                    {/* Test Voice */}
                    <button
                      onClick={testVoice}
                      disabled={testPlaying}
                      className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg border border-white/20 text-white hover:bg-white/5 transition-colors disabled:opacity-50"
                    >
                      {testPlaying ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Playing...
                        </>
                      ) : (
                        <>
                          <Play className="w-4 h-4" />
                          Test Voice
                        </>
                      )}
                    </button>

                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-white">Auto-play Responses</p>
                        <p className="text-sm text-white/50">Automatically read replies aloud</p>
                      </div>
                      <Toggle
                        enabled={settings.auto_play_responses}
                        onChange={(v) => setSettings({ ...settings, auto_play_responses: v })}
                      />
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Voice Input */}
            <div>
              <h3 className="text-sm font-medium text-white/50 uppercase tracking-wider mb-4">
                Voice Input
              </h3>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Mic className="w-5 h-5 text-white/60" />
                    <div>
                      <p className="text-white">Voice Commands</p>
                      <p className="text-sm text-white/50">Use microphone for input</p>
                    </div>
                  </div>
                  <Toggle
                    enabled={settings.voice_input_enabled}
                    onChange={(v) => setSettings({ ...settings, voice_input_enabled: v })}
                  />
                </div>

                {settings.voice_input_enabled && (
                  <>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-white">Wake Word</p>
                        <p className="text-sm text-white/50">Say "Hey Niki" to activate</p>
                      </div>
                      <Toggle
                        enabled={settings.wake_word_enabled}
                        onChange={(v) => setSettings({ ...settings, wake_word_enabled: v })}
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-white">Continuous Listening</p>
                        <p className="text-sm text-white/50">Keep microphone active</p>
                      </div>
                      <Toggle
                        enabled={settings.continuous_listening}
                        onChange={(v) => setSettings({ ...settings, continuous_listening: v })}
                      />
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Success */}
            {success && (
              <div className="bg-green-500/10 border border-green-500/30 rounded-lg px-4 py-3 flex items-center gap-3">
                <Check className="w-5 h-5 text-green-400 flex-shrink-0" />
                <p className="text-green-400 text-sm">Settings saved successfully!</p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-4 border-t border-white/10 flex gap-3 sticky bottom-0 bg-[#1a2e1a]">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-3 rounded-lg border border-white/20 text-white hover:bg-white/5 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex-1 px-4 py-3 rounded-lg bg-primary text-white font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Save
                </>
              )}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  )
}
